import moment from 'moment';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import {
	BellIcon,
	ConversationIcon,
	DialogueIcon,
	GridIcon,
	TargetIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	CYCLE_MOMENT_ROW_HEIGHT,
	CycleMomentOffset,
	CycleMomentsCard,
	type CycleMomentBarStatus,
	DateIntervalNavigation,
	type ITimeMachine,
} from '@treams/treams-ui';

import {
	ReminderModalContext,
	SkipCycleModalContext,
} from '../../../../manager/inbox/InboxManagerContainer';
import PopoverTarget from '../popovers/PopoverTarget';
import CycleMomentInstructionModal from './CycleMomentInstructionModal';
import CycleMomentPopover from './CycleMomentPopover';
import CycleMomentSendReminderModal from './CycleMomentSendReminderModal';
import CycleMomentSkipModal from './CycleMomentSkipModal';

interface IMomentRepresentable {
	startDate: Date;
	endDate: Date;
	title: string;
	cycle: graphql.CycleTemplate;
	cycleInstanceID: string;
	template: graphql.MomentTemplate;
	type: graphql.MomentType;
	instance?: graphql.MomentInstance;
	status: graphql.MomentStatus;
}

interface IWithIsDue {
	isDue: boolean;
}

function barStatus(momentInstance: IMomentRepresentable & IWithIsDue): CycleMomentBarStatus {
	if (momentInstance.status === graphql.MomentStatus.Completed) {
		return 'completed';
	}
	if (momentInstance.status === graphql.MomentStatus.Open && momentInstance.isDue) {
		return 'due';
	}
	return 'upcoming';
}

function momentKey(momentInstance: IMomentRepresentable, index: number) {
	return (
		momentInstance.instance?.id ??
		`${momentInstance.cycleInstanceID}-${momentInstance.template.id}-${momentInstance.startDate.toISOString()}-${index}`
	);
}

function CycleMoment({
	bounds,
	momentInstance,
	canSendReminder,
}: {
	bounds: [Date, Date];
	momentInstance: IMomentRepresentable & IWithIsDue;
	canSendReminder: boolean;
}) {
	const [showSkipModal, setShowSkipModal] = useState(false);
	const [showReminderModal, setShowReminderModal] = useState(false);

	const [instructionModal, setInstructionModal] = useState(
		undefined as
			| {
					title: string;
					instruction?: graphql.LocalizedAttributedString;
					buttonTitle: string;
					callback: () => void;
			  }
			| undefined,
	);

	const { onSendReminder } = useContext(ReminderModalContext);

	const { onShowSkip, isFromInbox } = useContext(SkipCycleModalContext);

	const onSendReminderHandler = () => {
		return canSendReminder && momentInstance.instance
			? () => {
					if (onSendReminder) {
						onSendReminder(momentInstance.instance!);
					} else {
						setShowReminderModal(true);
					}
				}
			: undefined;
	};

	const onShowSkipModal = () => {
		return momentInstance
			? () => {
					if (isFromInbox) {
						onShowSkip([{ ...momentInstance }]);
					} else {
						setShowSkipModal(true);
					}
				}
			: undefined;
	};

	return (
		<Box>
			<CycleMomentOffset
				bounds={bounds}
				startDate={momentInstance.startDate}
				endDate={momentInstance.endDate}
			>
				<PopoverTarget
					content={
						<CycleMomentPopover
							cycle={momentInstance.cycle}
							template={momentInstance.template}
							instance={momentInstance.instance}
							startDate={momentInstance.startDate}
							endDate={momentInstance.endDate}
							onSkip={onShowSkipModal()}
							onSendReminder={onSendReminderHandler()}
							showInstruction={(options) => setInstructionModal(options)}
						/>
					}
				>
					<div style={{ height: CYCLE_MOMENT_ROW_HEIGHT }} />
				</PopoverTarget>
			</CycleMomentOffset>
			{showSkipModal && (
				<CycleMomentSkipModal
					moments={[{ ...momentInstance }]}
					onDismiss={() => setShowSkipModal(false)}
				/>
			)}
			{showReminderModal && momentInstance.instance && (
				<CycleMomentSendReminderModal
					instance={momentInstance.instance}
					onDismiss={() => setShowReminderModal(false)}
				/>
			)}
			{instructionModal && (
				<CycleMomentInstructionModal
					title={instructionModal.title}
					instruction={instructionModal.instruction}
					buttonTitle={instructionModal.buttonTitle}
					onCancel={() => setInstructionModal(undefined)}
					onStart={() => {
						instructionModal.callback();
					}}
				/>
			)}
		</Box>
	);
}

function CycleMomentsPreviewPopovers({
	bounds,
	momentsWithDue,
	canSendReminder,
}: {
	bounds: [Date, Date];
	momentsWithDue: (IMomentRepresentable & IWithIsDue)[];
	canSendReminder: boolean;
}) {
	return (
		<>
			{momentsWithDue.map((momentInstance, i) => (
				<CycleMoment
					key={i}
					bounds={bounds}
					momentInstance={momentInstance}
					canSendReminder={canSendReminder}
				/>
			))}
		</>
	);
}

function MomentTypeIcon({ type }: { type: graphql.MomentType }) {
	switch (type) {
		case graphql.MomentType.Evaluation:
			return <ConversationIcon size={20} />;
		case graphql.MomentType.Feedback:
			return <DialogueIcon size={20} />;
		case graphql.MomentType.Goals:
			return <TargetIcon size={20} />;
		case graphql.MomentType.Message:
			return <BellIcon size={20} />;
		case graphql.MomentType.TalentReview:
			return <GridIcon size={20} />;
		default:
			return null;
	}
}

export default function CycleMomentsPreviewView({
	bounds,
	firstMomentDue,
	currentDate,
	momentsWithDue,
	mini,
	embedded,
	canSendReminder,
	loading,
	allowPagination,
	timeMachineValue,
	onChangeTimeMachine,
}: {
	bounds: [Date, Date];
	firstMomentDue?: Date;
	currentDate: Date;
	momentsWithDue: (IMomentRepresentable & IWithIsDue)[];
	mini: boolean;
	embedded?: boolean;
	canSendReminder: boolean;
	loading?: boolean;
	allowPagination?: boolean;
	timeMachineValue?: ITimeMachine;
	onChangeTimeMachine?: (value: ITimeMachine) => void;
	selected?: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	return (
		<CycleMomentsCard
			bounds={bounds}
			currentDate={currentDate}
			firstMomentDue={firstMomentDue}
			mini={mini}
			embedded={embedded}
			loading={loading}
			allowPagination={allowPagination}
			todayLabel={t('common.today')}
			emptyLabel={t('cycles.moment.preview.none')}
			rangeLabel={`${moment(bounds[0]).format('LL')} – ${moment(bounds[1]).format('LL')}`}
			formatHoverDate={(date) => moment(date).format('LL')}
			toolbar={
				timeMachineValue && onChangeTimeMachine ? (
					<DateIntervalNavigation
						value={timeMachineValue}
						onChange={onChangeTimeMachine}
						disableQuery
						allowFutureDates
					/>
				) : undefined
			}
			moments={momentsWithDue.map((momentInstance, index) => ({
				id: momentKey(momentInstance, index),
				startDate: momentInstance.startDate,
				endDate: momentInstance.endDate,
				title: localize(momentInstance.template.title).text,
				status: barStatus(momentInstance),
				icon: <MomentTypeIcon type={momentInstance.type} />,
			}))}
			overlay={
				!mini ? (
					<CycleMomentsPreviewPopovers
						bounds={bounds}
						momentsWithDue={momentsWithDue}
						canSendReminder={canSendReminder}
					/>
				) : undefined
			}
		/>
	);
}
